import { icons } from "./icons";
import type { SiteContent, SpiralItem, Social, Sponsor } from "./types";

const $ = (selector: string, root: ParentNode = document) =>
  root.querySelector<HTMLElement>(selector);

// icl it'd be pretty funny if someone tries to inject something sketchy into content.json
const esc = (s: unknown = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));

// resolve photo filename to its full path, absolute paths ("/photos/...") and full URLs ("https://...") ignored.
export function resolvePhoto(path: string): string {
  if (!path) return path;
  if (path.startsWith("/") || path.startsWith("http")) return path;
  return `/photos/${path}`;
}


export async function loadContent(): Promise<SiteContent> {
  try {
    const res = await fetch("./content.json", { cache: "no-cache" });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as SiteContent;
  } catch (e) {
    console.warn("[content] fallback:", e);
    return {};
  }
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function sponsorCard(sponsor: Sponsor): string {
  const glow = esc(sponsor.color || "#ff6a2b");
  const zoom = sponsor.logoZoom ?? 1;
  const lead = sponsor.tier === "lead" ? " sponsor--lead" : "";
  const logo = sponsor.logo
    ? `<img src="${esc(resolvePhoto(sponsor.logo))}" alt="${esc(sponsor.name)}" loading="lazy" />`
    : `<span class="sponsor__initials">${esc(initials(sponsor.name))}</span>`;
  const tag = sponsor.url ? "a" : "div";
  const href = sponsor.url ? `href="${esc(sponsor.url)}" target="_blank" rel="noopener" data-cursor="view"` : "";
  return `
    <${tag} class="sponsor${lead}" ${href} style="--glow:${glow};--logo-zoom:${zoom}">
      <div class="sponsor__logo">${logo}</div>
      <div class="sponsor__body">
        <span class="sponsor__since mono">partner since ${esc(sponsor.since)}</span>
        <h3 class="sponsor__name">${esc(sponsor.name)}</h3>
        <p class="sponsor__desc">${esc(sponsor.blurb)}</p>
        ${sponsor.url ? `<span class="sponsor__link mono">visit ${esc(sponsor.name)} <span class="sponsor__arrow">↗</span></span>` : ""}
      </div>
    </${tag}>`;
}

function socialRow(social: Social): string {
  return `
    <a class="social" href="${esc(social.url)}" target="_blank" rel="noopener"
       data-cursor="view" aria-label="${esc(social.name)}">
      ${icons[social.icon] ?? icons["linktree"]}
      <span class="social__txt">
        <span class="social__name">${esc(social.name)}</span>
        <span class="social__handle mono">${esc(social.handle)}</span>
      </span>
      <span class="social__arrow">↗</span>
    </a>`;
}

export function renderContent(data: SiteContent = {}): SiteContent {
  const set = (selector: string, value: unknown) => {
    const el = $(selector);
    if (el && value != null) el.textContent = String(value);
  };

  const socials = data.join?.socials ?? [];

  // ── hero ────────────────────────────────────────────────────────────────────
  set("#heroKicker", data.hero?.kicker);
  set("#heroBlurb", data.hero?.blurb);
  set("#spiralCount", (data.spiral ?? []).length);

  // ── menu email ──────────────────────────────────────────────────────────────
  const emailEl = $("#menuEmail");
  if (emailEl && data.society?.email) {
    (emailEl as HTMLAnchorElement).href = `mailto:${esc(data.society.email)}`;
    emailEl.textContent = `${data.society.email} ↗`;
  }

  // ── about page ──────────────────────────────────────────────────────────────
  set("#aboutLede", data.about?.lede);

  const colsEl = $("#aboutCols");
  if (colsEl && data.about?.columns) {
    colsEl.innerHTML = data.about.columns
      .map(
        (col) => `
        <article class="page__col">
          <span class="page__col-no mono">${esc(col.no)}</span>
          <h3>${esc(col.title)}</h3>
          <p>${esc(col.body)}</p>
        </article>`
      )
      .join("");
  }

  const statsEl = $("#aboutStats");
  if (statsEl && data.about?.stats) {
    statsEl.innerHTML = data.about.stats
      .map(
        (stat) => `
        <div class="page__stat">
          <span class="num" data-count="${esc(stat.value)}" data-suffix="${esc(stat.suffix ?? "")}">0</span>
          <span class="lbl mono">${esc(stat.label)}</span>
        </div>`
      )
      .join("");
  }

  set("#joinHeading", data.join?.heading);
  set("#joinBlurb", data.join?.blurb);

  const joinSocialsEl = $("#joinSocials");
  if (joinSocialsEl) joinSocialsEl.innerHTML = socials.map(socialRow).join("");

  // ── events page ─────────────────────────────────────────────────────────────
  if (data.events) {
    const headParts = [data.events.term, data.events.year].filter(Boolean);
    set("#eventsHead", headParts.join(", ").toLowerCase());
    set("#eventsNote", data.events.note);

    const lineEl = $("#eventsLine");
    if (lineEl && data.events.items) {
      lineEl.innerHTML = data.events.items
        .map((event) => {
          const isOffCampus = /off/i.test(event.location ?? "");
          return `
          <li class="stop">
            <span class="stop__dot" aria-hidden="true"></span>
            <div class="stop__top">
              <h3 class="stop__title">${esc(event.title)}</h3>
              <span class="stop__week">${esc(event.week ?? "")}</span>
            </div>
            <div class="stop__meta">
              <span>${esc(event.date ?? "")}</span>
              <span class="stop__tag ${isOffCampus ? "stop__tag--off" : ""}">${esc(event.location ?? "")}</span>
              ${event.collab ? `<span class="stop__collab">${esc(event.collab)}</span>` : ""}
            </div>
            ${event.blurb ? `<p class="stop__blurb">${esc(event.blurb)}</p>` : ""}
          </li>`;
        })
        .join("");
    }
  }

  // ── sponsors page ─────────────────────────────────────────────────────────────
  if (data.sponsors) {
    set("#sponsorsLede", data.sponsors.lede);

    const gridEl = $("#sponsorsGrid");
    if (gridEl && data.sponsors.items) {
      gridEl.innerHTML = data.sponsors.items.map(sponsorCard).join("");
    }

    const cta = data.sponsors.cta;
    const ctaEl = $("#sponsorsCta");
    if (cta) {
      set("#sponsorsCtaHeading", cta.heading);
      set("#sponsorsCtaBlurb", cta.blurb);
      set("#sponsorsCtaLabel", cta.label);
      const btn = $("#sponsorsCtaBtn") as HTMLAnchorElement | null;
      if (btn) btn.href = cta.url || (data.society?.email ? `mailto:${data.society.email}` : "#");
    } else if (ctaEl) {
      ctaEl.style.display = "none";
    }
  }

  // ── list view (newest first) ─────────────────────────────────────────────────
  const listEl = $("#listList");
  if (listEl && data.spiral) {
    const yearOf = (item: SpiralItem) => parseInt(String(item.year).replace(/\D/g, ""), 10) || 0;
    const ordered = data.spiral
      .map((item, index) => ({ item, index }))
      .sort((a, b) => yearOf(b.item) - yearOf(a.item));

    listEl.innerHTML = ordered
      .map(
        ({ item, index }) => `
        <li class="listview__item" data-list-i="${index}">
          <a href="#" data-cursor="view">
            <span class="listview__item-year mono">${esc(item.year ?? "")}</span>
            <span class="listview__item-title">${esc(item.title)}</span>
            <span class="listview__item-kind mono">${esc(item.kind ?? "")}</span>
            <span class="listview__item-arrow">↗</span>
          </a>
        </li>`
      )
      .join("");
  }

  return data;
}

// fill detail modal for a spiral item
export function fillModal(item: SpiralItem): void {
  const set = (selector: string, value: unknown) => {
    const el = $(selector);
    if (el) el.textContent = String(value ?? "");
  };

  set("#modalKind", item.kind || "entry");
  set("#modalTitle", item.title || "");
  set("#modalDesc", item.blurb || "");

  const tagsEl = $("#modalTags");
  if (tagsEl) {
    tagsEl.innerHTML = [item.year, ...(item.tags ?? [])]
      .filter(Boolean)
      .map((tag) => `<span class="modal__tag">${esc(tag)}</span>`)
      .join("");
  }

  const liveEl = $("#modalLive") as HTMLAnchorElement | null;
  if (liveEl) {
    if (item.link) {
      liveEl.href = item.link;
      liveEl.classList.remove("is-hidden");
    } else {
      liveEl.classList.add("is-hidden");
    }
  }

  const mediaEl = $("#modalMedia");
  if (!mediaEl) return;

  // genenrate video embeds if a id exists
  if (item.youtubeId) {
    const id = esc(item.youtubeId);
    mediaEl.innerHTML = `
      <img src="https://i.ytimg.com/vi/${id}/maxresdefault.jpg" alt="${esc(item.title)}" />
      <button class="yt-play" aria-label="Play video">
        <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
      </button>`;
    mediaEl.querySelector(".yt-play")?.addEventListener("click", () => {
      mediaEl.innerHTML = `
        <iframe
          src="https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0"
          title="${esc(item.title)}"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen>
        </iframe>`;
    });
  } else if (item.image) {
    mediaEl.innerHTML = `<img src="${esc(resolvePhoto(item.image))}" alt="${esc(item.title)}" />`;
  } else {
    const col = esc(item.color || "#ff6a2b");
    mediaEl.innerHTML = `
      <div class="modal__ph" style="background:radial-gradient(80% 120% at 70% 25%, ${col}66, transparent 60%), linear-gradient(135deg,#15171c,#0c0d11);">
        <span>${esc(item.year ?? "")}</span>
      </div>`;
  }
}
